/** What every container needs: the data source, the clock, the session and the translator. */
import { createContext, useContext } from "react";
import type { Locale, Translate } from "@faylasuf/i18n";
import type { DataSource, SessionInfo } from "../api/types.js";
import type { Boot } from "./bootstrap.js";

export interface AppContextValue {
  readonly boot: Boot;
  readonly source: DataSource;
  readonly now: () => Date;
  readonly locale: Locale;
  readonly t: Translate;
}

export const AppContext = createContext<AppContextValue | null>(null);

export function useApp(): AppContextValue {
  const v = useContext(AppContext);
  if (!v) throw new Error("useApp outside <AppContext>");
  return v;
}

export interface SessionContextValue {
  readonly session: SessionInfo;
  readonly hasPermission: (token: string) => boolean;
  readonly isSuperManager: boolean;
  /** Re-reads `GET /auth/session` (after enrolment, PIN change, …). */
  readonly refresh: () => Promise<unknown>;
  /** Any 401 from a container lands here: back to the login screen. */
  readonly sessionLost: () => void;
  readonly logout: () => Promise<void>;
}

export const SessionContext = createContext<SessionContextValue | null>(null);

export function useSession(): SessionContextValue {
  const v = useContext(SessionContext);
  if (!v) throw new Error("useSession outside <SessionContext>");
  return v;
}
